import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip, 
  Box, 
  Typography,
  OutlinedInput,
  ListSubheader,
  Checkbox,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import branding from '../../styles/branding';
import useApi from '../../hooks/useApi';
import { formatBusinessText } from '../../utils/textFormatting';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';
import './ColumnSelector.css';

const WIDGET_RECOMMENDED_MAP = {
  summary: ['freeText', 'score'],
  success: ['freeText', 'score'],
  custom_ai_card: ['freeText', 'score'],
  ai_recommendation: ['freeText', 'score'],
  major_issues: ['freeText', 'score'],
  kpi: ['score', 'numeric'],
};

const GROUP_CONFIG = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'categorical', label: 'Categorical' },
  { key: 'geographic', label: 'Geographic' },
  { key: 'general', label: 'General' },
  { key: 'identifier', label: 'Identifiers' },
];

function buildGroupedColumns(columnCategories, widgetType) {
  if (!columnCategories || Object.keys(columnCategories).length === 0) {
    return null;
  }

  const recommendedCategories = WIDGET_RECOMMENDED_MAP[widgetType] || ['freeText'];
  const recommendedSet = new Set(recommendedCategories);

  const recommended = [];
  for (const cat of recommendedCategories) {
    recommended.push(...(columnCategories[cat] || []));
  }

  const categorical = [];
  const geographic = columnCategories.geographic || [];
  const general = [];
  const identifier = columnCategories.identifier || [];

  for (const cat of ['score', 'numeric', 'categorical', 'freeText']) {
    if (recommendedSet.has(cat)) continue;
    const cols = columnCategories[cat] || [];
    if (cat === 'categorical') {
      categorical.push(...cols);
    } else {
      general.push(...cols);
    }
  }

  return { recommended, categorical, geographic, general, identifier };
}

const ColumnSelector = ({ selectedColumns, onColumnsChange, jobId, widgetType }) => {
  const [availableColumns, setAvailableColumns] = useState([]);
  const [columnCategories, setColumnCategories] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const api = useApi();
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    if (jobId) {
      fetchAvailableColumns();
    }
  }, [jobId]);

  const fetchAvailableColumns = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/jobs/${jobId}?getColumns=true`);

      const data = response.data?.data || response.data || {};
      const columns = data.columns || [];
      const categories = data.columnCategories || null;

      if (columns.length === 0) {
        console.warn('No columns returned from API, using fallback columns');
        setError('No columns found for this job, using common columns');
        setAvailableColumns([
          'ClientName', 'FacilityName', 'ServiceCategory', 'SubCategory',
          'FeedbackType', 'FeedbackRating', 'FeedbackComments',
          'PointerQuestionName', 'Answer', 'City', 'State', 'Department'
        ]);
        setColumnCategories(null);
      } else {
        setAvailableColumns(columns);
        setColumnCategories(categories);
      }
    } catch (err) {
      console.error('Error fetching columns:', err);
      setError(getApiErrorMessage(err, 'Failed to load available columns.'));
      setAvailableColumns([
        'ClientName', 'FacilityName', 'ServiceCategory', 'SubCategory',
        'FeedbackType', 'FeedbackRating', 'FeedbackComments',
        'PointerQuestionName', 'Answer', 'City', 'State', 'Department'
      ]);
      setColumnCategories(null);
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(
    () => buildGroupedColumns(columnCategories, widgetType),
    [columnCategories, widgetType]
  );

  const handleColumnChange = (event) => {
    const value = event.target.value;
    onColumnsChange(typeof value === 'string' ? value.split(',') : value);
  };

  const matchesSearch = (column) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    return column.toLowerCase().includes(term) || formatBusinessText(column).toLowerCase().includes(term);
  };

  const renderSearchHeader = () => (
    <ListSubheader
      key="search-header"
      sx={{ backgroundColor: '#fff', pt: 1, pb: 1, zIndex: 1 }}
    >
      <TextField
        size="small"
        autoFocus
        placeholder="Search columns..."
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        inputRef={searchInputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': { fontSize: '0.8rem' },
        }}
      />
    </ListSubheader>
  );

  const renderGroupedItems = () => {
    const items = [renderSearchHeader()];

    if (!grouped) {
      const filtered = availableColumns.filter(matchesSearch);
      if (filtered.length === 0) {
        items.push(
          <MenuItem key="no-results" disabled sx={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#999' }}>
            No columns match &quot;{searchTerm}&quot;
          </MenuItem>
        );
      } else {
        filtered.forEach((column) => {
          items.push(
            <MenuItem key={column} value={column}>
              <Checkbox checked={selectedColumns.indexOf(column) > -1} size="small" sx={{ p: 0.5, mr: 1 }} />
              {formatBusinessText(column)}
            </MenuItem>
          );
        });
      }
      return items;
    }

    let hasAnyVisible = false;
    for (const { key, label } of GROUP_CONFIG) {
      const cols = grouped[key];
      if (!cols || cols.length === 0) continue;

      const filtered = cols.filter(matchesSearch);
      if (filtered.length === 0) continue;

      hasAnyVisible = true;

      items.push(
        <ListSubheader
          key={`header-${key}`}
          className="column-group-header"
          sx={{
            fontFamily: 'Source Sans Pro, Arial, sans-serif',
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            lineHeight: '2rem',
            backgroundColor: '#f9f9f9',
            borderBottom: '1px solid #eee',
            pointerEvents: 'none',
          }}
        >
          {label}
        </ListSubheader>
      );

      for (const column of filtered) {
        items.push(
          <MenuItem key={column} value={column} sx={{ pl: 3 }}>
            <Checkbox checked={selectedColumns.indexOf(column) > -1} size="small" sx={{ p: 0.5, mr: 1 }} />
            {formatBusinessText(column)}
          </MenuItem>
        );
      }
    }

    if (!hasAnyVisible) {
      items.push(
        <MenuItem key="no-results" disabled sx={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#999' }}>
          No columns match &quot;{searchTerm}&quot;
        </MenuItem>
      );
    }

    return items;
  };

  return (
    <Box className="column-selector-container">
      <Typography
        variant="subtitle2"
        className="column-selector-label"
        sx={{ mb: 1, minHeight: '1.25rem', fontSize: '0.75rem', fontWeight: 600, color: '#666666', fontFamily: branding.fonts.primary }}
      >
        Select Columns:
      </Typography>
      <FormControl fullWidth sx={{ mt: 0 }}>
        <InputLabel id="column-selector-label">Columns</InputLabel>
        <Select
          labelId="column-selector-label"
          multiple
          value={selectedColumns}
          onChange={handleColumnChange}
          open={menuOpen}
          onOpen={() => { setMenuOpen(true); setSearchTerm(''); }}
          onClose={() => { setMenuOpen(false); setSearchTerm(''); }}
          input={<OutlinedInput label="Columns" />}
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              minWidth: 0,
            },
          }}
          renderValue={(selected) => {
            if (!selected?.length) return '';
            const extra = selected.length - 1;
            const firstLabel = formatBusinessText(selected[0]);
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Typography
                  component="span"
                  variant="body2"
                  noWrap
                  title={firstLabel}
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {firstLabel}
                </Typography>
                {extra > 0 && (
                  <Chip
                    label={`+${extra} ${extra === 1 ? 'column' : 'columns'}`}
                    size="small"
                    sx={{ flexShrink: 0, bgcolor: 'rgba(0,0,0,0.08)' }}
                  />
                )}
              </Box>
            );
          }}
          disabled={loading}
          MenuProps={{
            PaperProps: {
              ...branding.menu?.PaperProps,
              sx: { ...branding.menu?.PaperProps?.sx, maxHeight: 360 },
            },
            autoFocus: false,
            disableAutoFocusItem: true,
          }}
        >
          {renderGroupedItems()}
        </Select>
      </FormControl>
      {loading && (
        <Typography variant="caption" color="text.secondary" className="column-selector-loading" sx={{ mt: 0.5, minHeight: '1rem' }}>
          Loading columns...
        </Typography>
      )}
      {!error && !loading && (
        <Box sx={{ minHeight: '1rem', mt: 0.5 }} />
      )}
    </Box>
  );
};

export default ColumnSelector;
